import { useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { Check, Lock } from "lucide-react";
import { login, storeSession } from "../../shared/api/auth";
import {
  platformApi,
  isPlatformSuperadmin,
  setPlatformAdminFlag,
} from "../../shared/api/platform";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type FieldErrors = Partial<Record<"email" | "password", string>>;

const HIGHLIGHTS = [
  "Lịch hẹn, khám bệnh, đơn thuốc, hoá đơn — một chỗ.",
  "Tiêm chủng, tồn kho, báo cáo doanh thu theo thời gian thực.",
  "Ký số, e-đơn thuốc, tuân thủ quy định y tế Việt Nam.",
];

/** Reference page for the Tailwind + shadcn migration (ADR-0026): split brand/form
 *  layout inspired by modern admin themes, Addy brand (Montserrat/Inter · orange · navy). */
export function LoginPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);

  function validate(): FieldErrors {
    const next: FieldErrors = {};
    if (!email.trim()) next.email = "Vui lòng nhập email hoặc tên đăng nhập.";
    if (!password) next.password = "Vui lòng nhập mật khẩu.";
    return next;
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFormError(null);
    const v = validate();
    setErrors(v);
    if (Object.keys(v).length > 0) return;

    setSubmitting(true);
    try {
      const result = await login({ username: email.trim(), password });
      if (result.kind === "mfa") {
        sessionStorage.setItem("addy.mfaToken", result.mfaToken);
        navigate("/mfa-verify");
        return;
      }
      storeSession(result);
      // Drop any queries cached under a previous session on this tab (e.g. reached /login via
      // token-expiry without an explicit logout) so the new user never sees stale /me data.
      qc.clear();
      try {
        const me = await platformApi.me();
        const isPlatform = isPlatformSuperadmin(me.roleCodes);
        setPlatformAdminFlag(isPlatform);
        navigate(isPlatform ? "/platform/tenants" : "/dashboard");
      } catch {
        setPlatformAdminFlag(false);
        navigate("/dashboard");
      }
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Đăng nhập thất bại.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen font-sans">
      {/* Brand panel — navy, hidden on small screens */}
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-[#10213d] p-12 text-white lg:flex">
        <div className="pointer-events-none absolute -left-20 -top-20 h-72 w-72 rounded-full bg-primary/25 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-16 right-0 h-56 w-56 rounded-full bg-primary/10 blur-2xl" />
        <div className="pointer-events-none absolute right-24 top-24 h-24 w-24 rounded-full border border-white/10" />

        <div className="relative">
          <img
            src="/addy-logo.png"
            alt="Addy Care"
            className="h-10 w-auto brightness-0 invert"
          />
        </div>

        <div className="relative">
          <h1 className="text-4xl font-bold leading-tight">
            Quản lý phòng khám
            <br />
            thông minh, gọn nhẹ.
          </h1>
          <p className="mt-4 max-w-md text-white/70">
            Hệ thống thông tin lâm sàng cho phòng khám ngoại trú tại Việt Nam.
          </p>
          <ul className="mt-8 space-y-3">
            {HIGHLIGHTS.map((h) => (
              <li key={h} className="flex items-start gap-3 text-sm text-white/85">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/20">
                  <Check className="h-3.5 w-3.5 text-primary" />
                </span>
                {h}
              </li>
            ))}
          </ul>
        </div>

        <div className="relative text-xs text-white/40">
          © {new Date().getFullYear()} Addy HIS · ID Solutions
        </div>
      </div>

      {/* Form panel */}
      <div className="flex w-full items-center justify-center bg-background p-4 lg:w-1/2">
        <div className="w-full max-w-md">
          <div className="mb-8 flex justify-center lg:hidden">
            <img src="/addy-logo.png" alt="Addy Care" className="h-9 w-auto" />
          </div>

          <h2 className="text-2xl font-bold text-foreground">Đăng nhập</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Tiếp tục với tài khoản Addy HIS của phòng khám.
          </p>

          <form className="mt-6 space-y-4" onSubmit={onSubmit} noValidate>
            <div className="space-y-2">
              <Label htmlFor="email">Email / Tên đăng nhập</Label>
              <Input
                id="email"
                type="text"
                autoComplete="username"
                placeholder="bacsi@phongkhamcuaban.vn hoặc tên đăng nhập"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-invalid={Boolean(errors.email)}
                disabled={submitting}
              />
              {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Mật khẩu</Label>
                <Link to="/forgot-password" className="text-sm text-primary hover:underline">
                  Quên mật khẩu?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="Tối thiểu 8 ký tự"
                  className="pr-14"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  aria-invalid={Boolean(errors.password)}
                  disabled={submitting}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
                  disabled={submitting}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-sm font-medium text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? "Ẩn" : "Hiện"}
                </button>
              </div>
              {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4 accent-primary"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                disabled={submitting}
              />
              Ghi nhớ thiết bị này trong 30 ngày
            </label>

            {formError && (
              <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {formError}
              </div>
            )}

            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? "Đang xác thực…" : "Đăng nhập"}
            </Button>

            <div className="flex items-start gap-2 rounded-md bg-muted p-3 text-xs text-muted-foreground">
              <Lock className="mt-0.5 shrink-0 text-primary" />
              <div>
                <strong className="text-foreground">MFA bắt buộc cho tài khoản chủ phòng khám.</strong>{" "}
                Sau khi nhập đúng mật khẩu bạn sẽ được yêu cầu mã TOTP 6 chữ số.
              </div>
            </div>

            <p className="text-center text-sm text-muted-foreground">
              Chưa có tài khoản?{" "}
              <Link to="/register" className="font-medium text-primary hover:underline">
                Đăng ký phòng khám mới
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
